// A missing percentage (no predecessor, or a predecessor of 0) is an em dash.
// Zero is a real measurement and must not collapse into it.
import { EM_DASH } from './rfmAnalysis';

// The stages an email/campaign stats funnel flows through, top to bottom. This
// list used to live inside FunnelChart, which silently dropped anything not on
// it — that made the component unable to draw a user-defined funnel. It is the
// email funnel's own ordering, so it belongs to that caller.
export const EMAIL_FUNNEL_STAGE_ORDER = ['Sent', 'Deliveries', 'Opens', 'Clicks', 'Conversions', 'Purchases'];

/**
 * Order the per-action stats of an email/campaign into funnel steps: the known
 * stages in flow order, dropping any action that is not one of them, with each
 * step's share of its predecessor attached.
 * @param {Array<{label: string, value: number}>} [stages] - one entry per action, in any order
 * @returns {Array<{label: string, value: number, percentOfPrevious: number|null}>}
 */
export const buildEmailFunnelSteps = (stages) => {
  const byLabel = new Map((stages || []).map((stage) => [stage.label, stage]));
  const ordered = EMAIL_FUNNEL_STAGE_ORDER.filter((label) => byLabel.has(label)).map((label) => byLabel.get(label));

  return ordered.map((stage, index) => ({
    label: stage.label,
    value: stage.value,
    // Same "no baseline" convention the funnels API applies: the first step has
    // no predecessor, and a previous step of 0 makes the ratio meaningless.
    percentOfPrevious: index > 0 && ordered[index - 1].value ? (stage.value / ordered[index - 1].value) * 100 : null,
  }));
};

/**
 * Format a step's share of its predecessor, to one decimal.
 * @param {number|null} [value]
 * @returns {string} e.g. "12.9%", or an em dash when there is no baseline
 */
export const formatFunnelPercent = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : EM_DASH;

/**
 * Abbreviate a step count for the label drawn inside its bar, where the exact
 * figure would not fit.
 * @param {number} value
 * @returns {string}
 */
export const formatFunnelCount = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};
